// @ts-nocheck ts(2834)
/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";

import {
    PRIORITY_PRELOAD_COMPONENTS,
    componentLoaders,
    UNIFORM_TYPE_TO_LOADER
} from "./component-loaders";
import { UniformMissingComponent } from "./utils/uniform-missing-component";

// Component type for better type safety
type UniformComponent = React.ComponentType<any>;

// ALL components are lazy loaded - no eager imports to bloat bundles!
const UNIFORM_DEFINITIONS: Record<
    string,
    React.LazyExoticComponent<UniformComponent>
> = {};

// Map each component type to lazy-loaded component
Object.entries(UNIFORM_TYPE_TO_LOADER).forEach(([uniformType, loaderKey]) => {
    const loader = componentLoaders[loaderKey];
    if (loader) {
        const isPriority = PRIORITY_PRELOAD_COMPONENTS.has(uniformType);
        UNIFORM_DEFINITIONS[uniformType] = React.lazy(() =>
            loader()
                .then((module) => {
                    if (module && module.default) {
                        return { default: module.default };
                    } else {
                        return null;
                    }
                })
                .catch((error) => {
                    return null;
                })
        );
    }
});

/**
 * Loads a component dynamically based on its type
 * @param type - The Uniform component type
 * @returns Promise that resolves to the component or null if not found
 */
export async function loadComponent(
    type: string
): Promise<UniformComponent | null> {
    const loaderKey = UNIFORM_TYPE_TO_LOADER[type];
    if (!loaderKey) {
        return null;
    }

    const loader = componentLoaders[loaderKey];
    if (!loader) {
        return null;
    }

    try {
        const module = await loader();
        if (module && module.default) {
            return module.default;
        } else {
            throw new Error(`No default export found for component: ${type}`);
        }
    } catch (error) {
        return UniformMissingComponent;
    }
}

/**
 * Extracts all component types from a composition recursively
 */
function extractComponentTypes(composition: any): Set<string> {
    const types = new Set<string>();

    function traverse(node: any) {
        if (node?.type) {
            types.add(node.type);
        }

        if (node?.slots) {
            Object.values(node.slots).forEach((slotItems: any) => {
                if (Array.isArray(slotItems)) {
                    slotItems.forEach(traverse);
                }
            });
        }
    }

    traverse(composition);
    return types;
}

/**
 * Preloads all components found in a given composition
 */
export async function preloadCompositionComponents(
    composition: any
): Promise<void> {
    if (!composition) return;

    const componentTypes = extractComponentTypes(composition);
    const typesToPreload = Array.from(componentTypes).filter(
        (type) => UNIFORM_TYPE_TO_LOADER[type]
    );

    if (typesToPreload.length === 0) return;

    await Promise.all(
        typesToPreload.map(async (type) => {
            const loaderKey = UNIFORM_TYPE_TO_LOADER[type];
            if (loaderKey && componentLoaders[loaderKey]) {
                try {
                    await componentLoaders[loaderKey]();
                } catch (error) {
                    console.warn(`Failed to preload component: ${type}`, error);
                }
            }
        })
    );
}

/**
 * Preloads priority components that exist in a given composition
 */
export async function preloadPriorityComponentsInComposition(
    composition: any
): Promise<void> {
    if (!composition) return;

    const componentTypes = extractComponentTypes(composition);
    const priorityTypesInComposition = Array.from(componentTypes).filter(
        (type) =>
            PRIORITY_PRELOAD_COMPONENTS.has(type) &&
            UNIFORM_TYPE_TO_LOADER[type]
    );

    if (priorityTypesInComposition.length === 0) return;

    await Promise.all(
        priorityTypesInComposition.map(async (type) => {
            const loaderKey = UNIFORM_TYPE_TO_LOADER[type];
            if (loaderKey && componentLoaders[loaderKey]) {
                try {
                    await componentLoaders[loaderKey]();
                } catch (error) {
                    console.warn(
                        `Failed to preload priority component: ${type}`,
                        error
                    );
                }
            }
        })
    );
}

export default UNIFORM_DEFINITIONS;
